/**
 * List Collection Section - Mobile & Desktop Carousel
 * Touch/swipe functionality for mobile devices and desktop carousel when more than 8 blocks
 */

class ListCollectionCarousel {
  constructor() {
    this.section = document.querySelector('.list-collection-section');
    this.grid = document.querySelector('.list-collection-section__grid');
    this.wrapper = document.querySelector('.list-collection-section__wrapper');
    this.cards = document.querySelectorAll('.list-collection-section__card');
    
    if (!this.section || !this.grid || this.cards.length === 0) {
      return;
    }
    
    this.isMobile = window.innerWidth <= 1024;
    this.isDesktopCarousel = window.innerWidth > 1024 && this.cards.length > 8;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.scrollLeft = 0;
    
    this.init();
  }
  
  init() {
    this.setupDesktopCarousel();
    this.setupEventListeners();
    this.handleResize();
  }
  
  setupDesktopCarousel() {
    if (this.isDesktopCarousel) {
      this.grid.classList.add('desktop-carousel');
      this.wrapper.classList.add('desktop-carousel');
    } else {
      this.grid.classList.remove('desktop-carousel');
      this.wrapper.classList.remove('desktop-carousel');
    }
  }
  
  setupEventListeners() {
    // Touch events for mobile and desktop carousel
    this.grid.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    this.grid.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.grid.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: false });
    
    // Mouse events for desktop carousel
    this.grid.addEventListener('mousedown', this.handleMouseDown.bind(this));
    this.grid.addEventListener('mousemove', this.handleMouseMove.bind(this));
    this.grid.addEventListener('mouseup', this.handleMouseUp.bind(this));
    this.grid.addEventListener('mouseleave', this.handleMouseUp.bind(this));
    
    // Window resize
    window.addEventListener('resize', this.handleResize.bind(this));
    
    // Prevent default touch behavior only for non-link elements
    this.grid.addEventListener('touchstart', (e) => {
      if (this.isMobile || this.isDesktopCarousel) {
        const target = e.target.closest('a');
        if (!target) {
          e.preventDefault();
        }
      }
    });
  }
  
  handleTouchStart(e) {
    if (!this.isMobile && !this.isDesktopCarousel) return;
    
    // Disable animations during touch
    this.disableAnimations();
    
    // Check if the touch target is a link
    const target = e.target.closest('a');
    if (target) {
      // Allow link clicks to work normally
      return;
    }
    
    this.isDragging = true;
    this.startX = e.touches[0].clientX;
    this.currentX = this.startX;
    this.scrollLeft = this.grid.scrollLeft;
    
    this.grid.style.scrollBehavior = 'auto';
  }
  
  handleTouchMove(e) {
    if ((!this.isMobile && !this.isDesktopCarousel) || !this.isDragging) return;
    
    // Check if the touch target is a link
    const target = e.target.closest('a');
    if (target) {
      // Allow link clicks to work normally
      this.isDragging = false;
      return;
    }
    
    e.preventDefault();
    
    this.currentX = e.touches[0].clientX;
    const deltaX = this.currentX - this.startX;
    const newScrollLeft = this.scrollLeft - deltaX;
    
    this.grid.scrollLeft = newScrollLeft;
  }
  
  handleTouchEnd(e) {
    if ((!this.isMobile && !this.isDesktopCarousel) || !this.isDragging) return;
    
    this.isDragging = false;
    this.grid.style.scrollBehavior = 'smooth';
    
    // Re-enable animations after touch
    this.enableAnimations();
    
    // Snap to nearest card
    this.snapToNearestCard();
  }
  
  handleMouseDown(e) {
    if (!this.isDesktopCarousel) return;
    
    this.isDragging = true;
    this.startX = e.clientX;
    this.currentX = this.startX;
    this.scrollLeft = this.grid.scrollLeft;
    
    this.grid.style.scrollBehavior = 'auto';
    this.grid.style.cursor = 'grabbing';
  }
  
  handleMouseMove(e) {
    if (!this.isDesktopCarousel || !this.isDragging) return;
    
    e.preventDefault();
    
    this.currentX = e.clientX;
    const deltaX = this.currentX - this.startX;
    const newScrollLeft = this.scrollLeft - deltaX;
    
    this.grid.scrollLeft = newScrollLeft;
  }
  
  handleMouseUp(e) {
    if (!this.isDesktopCarousel || !this.isDragging) return;
    
    this.isDragging = false;
    this.grid.style.scrollBehavior = 'smooth';
    this.grid.style.cursor = 'grab';
    
    // Snap to nearest card
    this.snapToNearestCard();
  }
  
  disableAnimations() {
    this.cards.forEach(card => {
      card.style.transition = 'none';
      card.style.transform = 'none';
    });
  }
  
  enableAnimations() {
    this.cards.forEach(card => {
      card.style.transition = '';
      card.style.transform = '';
    });
  }
  
  snapToNearestCard() {
    if (!this.isMobile && !this.isDesktopCarousel) return;
    
    const cardWidth = this.cards[0]?.offsetWidth || 150;
    const gap = this.isMobile ? 6 : 16; // Different gaps for mobile vs desktop
    const cardWithGap = cardWidth + gap;
    const scrollLeft = this.grid.scrollLeft;
    const snapPosition = Math.round(scrollLeft / cardWithGap) * cardWithGap;
    
    this.grid.scrollTo({
      left: snapPosition,
      behavior: 'smooth'
    });
  }
  
  handleResize() {
    const wasMobile = this.isMobile;
    const wasDesktopCarousel = this.isDesktopCarousel;
    
    this.isMobile = window.innerWidth <= 1024;
    this.isDesktopCarousel = window.innerWidth > 1024 && this.cards.length > 8;
    
    if (wasMobile !== this.isMobile || wasDesktopCarousel !== this.isDesktopCarousel) {
      this.grid.scrollLeft = 0;
      this.setupDesktopCarousel();
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  const sections = document.querySelectorAll('.list-collection-section');
  sections.forEach(section => {
    new ListCollectionCarousel();
  });
});

// Re-initialize when new content is loaded
if (typeof window.MutationObserver !== 'undefined') {
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'childList') {
        const addedNodes = Array.from(mutation.addedNodes);
        const hasNewSection = addedNodes.some(node => 
          node.nodeType === 1 && 
          (node.classList?.contains('list-collection-section') || 
           node.querySelector?.('.list-collection-section'))
        );
        
        if (hasNewSection) {
          setTimeout(() => {
            const sections = document.querySelectorAll('.list-collection-section');
            sections.forEach(section => {
              if (!section.dataset.carouselInitialized) {
                new ListCollectionCarousel();
                section.dataset.carouselInitialized = 'true';
              }
            });
          }, 100);
        }
      }
    });
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}